import { ReactComponent as IconDelete } from "@/assets/svgs/Delete.svg";
import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as ExclamationCircleLight } from '@/assets/svgs/exclamation-circle-light.svg';
import { ReactComponent as ExclamationCircle } from '@/assets/svgs/exclamation-circle.svg';
import { ReactComponent as IconClose } from "@/assets/svgs/icon-close.svg";
import { ReactComponent as IconPadLock } from '@/assets/svgs/icon-pad-lock.svg';
import { ReactComponent as IconPlus } from "@/assets/svgs/icon-plus.svg";
import { ReactComponent as IconQr } from '@/assets/svgs/icon-qr.svg';
import { ReactComponent as IconSmartPhone } from '@/assets/svgs/icon-smartphone.svg';
import { ReactComponent as InformationCircle } from '@/assets/svgs/information-circle.svg';
import { ReactComponent as IconProfile } from "@/assets/svgs/profile-default.svg";
import { ReactComponent as IconTickCircle } from '@/assets/svgs/tick-circle.svg';
import { ReactComponent as IconTickEmptyCircle } from '@/assets/svgs/tick-empty-circle.svg';
import { ReactComponent as IconTick } from '@/assets/svgs/tick.svg';
import { ReactComponent as IconTrash } from '@/assets/svgs/trash.svg';
import { ReactComponent as IconWarningTrash } from '@/assets/svgs/my-info-business-error.svg';
import { ReactComponent as DeleteIcon } from '@/assets/svgs/trashIcon.svg';
import { ReactComponent as AssignGroupIcon } from "@/assets/svgs/assign-group.svg";
import Button from "@/components/Button/index";
import { DataTable3 } from "@/components/DataTable3/index";
import Input from "@/components/Input/index";
import Toast from '@/components/Toast/index';
import ToggleSwitch3 from '@/components/ToggleSwitch3/index.js';
// [DS-MIGRATION] legacy Breadcrumbs - replaced by DS-backed Breadcrumb. Remove in follow-up PR.
// import Breadcrumbs from "@/componentsDS/Breadcrumbs/index";
import Breadcrumb from '@/dsComponents/Breadcrumb';
import CardConfigure from "@/componentsDS/Modal/CardConfigure";
import Modal from "@/componentsDS/Modal/Modal";
import { makerCheckerRolePermission } from "@/constants/maker-checker/index";
import useAuthLogin from '@/hooks/use-auth-login';
import { useBuildex } from "@/hooks/use-buildex";
import { useHealthdex } from "@/hooks/use-healthdex";
import { useTradex } from "@/hooks/use-tradex";
import useDebounce from "@/hooks/use-debounce/index";
import useModal from "@/hooks/use-modal";
import usePage from "@/hooks/use-page";
import useRole from "@/hooks/use-role";
import useToast from '@/hooks/use-toast/index';
import useUserManagement from "@/hooks/use-user-management/index";
import { useLocation, useNavigate, useParams } from "@/lib/router-dom/index";
import { logout, syncProfile } from '@/redux/actions/auth.js';
import OrgAPI from '@/services/api/org';
import TwoFactorAuthenticationApi from "@/services/api/twoFactorAuthenticatication";
import api from "@/services/api/userManagement";
import groupApi from "@/services/api/organizationGroup";
import { getEnvAndDex } from "@/utils/get-env-and-dex";
import { validateDomain } from '@/utils/validate-domain.js';
import { validateDomain as validEmailDomain, validateEmail } from "@/utils/validate-email";
import classname from 'classnames';
import { produce } from "immer";
import { cloneDeep, toLower, uniq, upperFirst, noop } from "lodash";
import moment from "moment";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { featureConstant } from '@/constants/configuration/feature';
import { useFeature } from "@/hooks/use-feature/index.js";
import ButtonDS from "@/componentsDS/Button/Button";
import { IconSize, IconStyle } from "@/componentsDS/Icon/Icon.type";
import { ButtonType, ButtonVariant } from "@/componentsDS/Button/Button.type";
import Icon from "@/componentsDS/Icon/Icon";
import InputDropdownDS from '@/dsComponents/InputDropdownSelect/index'
import { Box, Checkbox as DSCheckbox, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Select, SelectContent } from '@dextech-ai/dex-design-system'
import { applySelectedValues, getSelectedValues, renderSelectItems, renderValueOptions, selectedOptionValue } from '@/utils/select-items'

const hideEtrRoleForOtherDex  =(roles, isTradex) =>{
	if(roles.length>0 && !isTradex){
		return roles.filter((userRole) => userRole.label.toLowerCase() !== 'etr user')
	}
	return roles
}

// The table renders header/cell as components, so they live outside
// UserManagement: a new function per render would remount the checkbox on
// every click and skip its check animation.
const SelectAllHeader = ({ table }) => (
	<Box className="user-management__select-checkbox">
		<DSCheckbox
			data-testid="user-management-select-all"
			checked={table.getIsAllRowsSelected()}
			indeterminate={table.getIsSomeRowsInPageSelected()}
			disabled={table?.options?.meta?.editMode}
			onCheckedChange={(checked) => table.toggleAllRowsSelected(checked)}
		/>
	</Box>
)

const SelectRowCell = ({ row, table }) => (
	<Box className="user-management__select-checkbox">
		<DSCheckbox
			data-testid="user-management-select"
			checked={row.getIsSelected()}
			disabled={!row.getCanSelect() || table?.options?.meta?.editMode}
			onCheckedChange={(checked) => row.toggleSelected(checked)}
		/>
	</Box>
)

const UserManagement = ({ role }) => {

	/*=================================================1
		General
	/================================================*/
	const isSoftDeleteUserEnabled = useFeature(featureConstant.SOFT_DELETE_USER);
	const googleStoreAuthenticatorLink = "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
	const ituneStoreAuthenticatorLink = "https://apps.apple.com/us/app/google-authenticator/id388497605"
	const googleStoreMicrosoftAuthenticatorLink = "https://play.google.com/store/apps/details?id=com.azure.authenticator"
	const ituneStoreMicrosoftAuthenticatorLink = "https://apps.apple.com/us/app/microsoft-authenticator/id983156458"

	const { currentState } = useLocation();
	const location = currentState?.state

	const [pageTitle] = useState("User Management");

	const dispatch = useDispatch()

	const profile = useSelector(state => state.auth.profile)
	const { dex } = getEnvAndDex();
	const isBuildex = useBuildex();
	const isHealthdex = useHealthdex();
	const isTradex = useTradex();
	const isUserAccessManagementPhase2Enabled = useFeature(featureConstant.USER_ACCESS_MANAGEMENT_PHASE_2);

	const { breadcrumbsItems, setBreadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs:[
			{
				label: '',
				link: `/`,
			},
			{
				label: pageTitle,
				link: ``
			},
		]
	});

	const toastType = {
		success: "success",
		error: "error"
	}

	const { organizationId } = useParams();

	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);

	const [deleteItem, setDeleteItem] = useState(null);

	const [searchUser, setSearchUser] = useState();

	const [editItem, setEditItem] = useState({});
	const [editUserRole, setEditUserRole] = useState([]);
	const [addedRole, setAddedRole] = useState([])
	const [removedRole, setRemovedRole] = useState([])
	const checkProfile = profile?.organization?.tfa ?? true
	const orgId = profile?.organizationId
	const organisationHasEnabledMakerChecker = profile?.organization?.makerChecker ?? false

	const [pageLimit, setPageLimit] = useState(5);
	const [errorInput, setErrorInput] = useState();

	const [sortConfig, setSortConfig] = useState({ sortBy: "created", direction: false });

	const { goTo } = useNavigate();

	const [submitLoading, setSubmitLoading] = useState()
	const [orgGroupsData, setOrgGroupsData] = useState([]);

	const [dropdownSearch, setDropdownSearch] = useState({ groupSearch: '' })
	const [chipValues, setChipValues] = useState([]);
	const [chipValuesForCreate, setChipValuesForCreate] = useState([]);


	const dropdownSearchHandler = (e) => {
		if (e?.target) { setDropdownSearch(prev => ({ ...prev, [e.target.name]: e.target.value })); }
	}

	const transformedGroups = useMemo(() => {
		return orgGroupsData.map((group) => {
			const dashboardNames = (group?.dashboards ?? []).map(d => d?.name?.trim()).filter(Boolean);
			return {
				...group,
				label: group.name,
				tooltipInfo: (
					<div className="user-management__tooltip-content">
						<span>Dashboard:</span>
						<ul>
							<li>{dashboardNames.length ? dashboardNames.join(', ') : 'No dashboard linked to this group'}</li>
						</ul>
						{/* Data Elements - will be available in future */}
					</div>
				)
			}
		})
	}, [orgGroupsData]);
	
	useEffect(() => {
		if (!isBuildex || !isUserAccessManagementPhase2Enabled) return;

		const fetchOrgGroups = async () => {
			try {
				const [errorResponse, dataResponse] = await groupApi.list(organizationId);

				if (errorResponse) {
					addToastMessage(errorResponse?.message, toastType.error)
					return;
				}

				const groups = (dataResponse ?? []).map(({ id, name, dashboards }) => ({
					id,
					name,
					dashboards: dashboards ?? [],
				}));

				setOrgGroupsData(groups);
			} catch (e) {
				addToastMessage(e?.message, toastType.error)
			}
		};

		fetchOrgGroups();
	}, [isBuildex, isUserAccessManagementPhase2Enabled]);
	
	/*=================================================
		Feature
	/================================================*/


	/*=================================================
		Modal
	/================================================*/
	const { visible: isDeleteVisible, show: showDeleteModal, hide: hideDeleteModal } = useModal();
	const { visible: isEditVisible, hide: hideEditModal } = useModal();
	const { visible: enableTfa, show: showEnableTfa, hide: hideEnableTfa } = useModal();
	const { visible: successfulTfa, show: showSuccessfulTfa } = useModal();
	const { visible: learnMoreTfa, show: showLearnMoreTfa, hide: hideLearnMoreTfa } = useModal();
	const { visible: disableTfa, show: showDisableTfa, hide: hideDisableTfa } = useModal();
	const { visible: verifyTfa, show: showVerifyTfa, hide: hideVerifyTfa } = useModal();
	const { visible: learnMoreMekerCheckerModal, show: showLearnMoreMekerCheckerModal, hide: hideLearnMoreMekerCheckerModal } = useModal();
	const { visible: verifyMakerCheckerModal, show: showVerifyMakerCheckerModal, hide: hideVerifyMakerCheckerModal } = useModal()
	const { visible: disableMakerCheckerModal, show: showDisableMakerCheckerModal, hide: hideDisableMakerCheckerModal } = useModal()
	const { visible: errorUserModal, show: showErrorUserModal, hide: hideErrorUserModal } = useModal()
	const { visible: isAssignGroupVisible, show: showAssignGroupModal, hide: hideAssignGroupModal } = useModal()
	const [errorUserMessage, setErrorUserMessage] = useState("");

	/*=================================================
		Disable Two-Factor Authentication Popup
	/================================================*/
	const referenceRef = useRef(null)
	const redirectURL = encodeURI(window.location.origin + `/auth/redirect`);

	useEffect(() => {
		document.addEventListener('mousedown', handleDocumentClick)
		return () => {
			document.removeEventListener('mousedown', handleDocumentClick)
		}
	}, [])

	function handleDocumentClick(event) {

		if (!referenceRef.current) {
			return
		}

		if (referenceRef.current.contains(event.target)) {
			return
		}
	}

	function handledEnableTfa(checked) {
		if (checked) {
			showEnableTfa()
		} else {
			showDisableTfa()
		}
	}

	/*=================================================
		API Maker-Checker
	/================================================*/
	async function handleEnableMakerChecker(changedValues) {
		setSubmitLoading(true)

		const users = Object.keys(changedValues).map((userId) => {
			return {
				id: userId,
				makerCheckerRole: changedValues[userId]?.makerCheckerRole?.toUpperCase(),
			}
		}).filter((user) => ['MAKER', 'CHECKER'].includes(user.makerCheckerRole));
		const payload = {
			"makerCheckerStatus": true,
			"users": users
		}

		const [error] = await OrgAPI.updateStatusMakerChecker(orgId, payload);

		if (error) {
			setMakerCheckerToggle(false)
			return addToastMessage(error?.message, toastType.error)
		}

		setAtLeastOneUserMakerChecker(true)

		addToastMessage("You have successfully enabled Maker Checker.", toastType.success)

		getSortedData()
		dataTableRef?.current?.setEditMode(false);

		await dispatch(syncProfile())
		setSubmitLoading(false)
	}

	async function handleDisableMakerChecker() {
		setSubmitLoading(true)

		const payload = {
			"makerCheckerStatus": false,
		}

		const [error] = await OrgAPI.updateStatusMakerChecker(orgId, payload);

		if (error) {
			setMakerCheckerToggle(false)
			setSubmitLoading(false)
			return addToastMessage(error?.message, toastType.error)
		}

		setAtLeastOneUserMakerChecker(true)

		addToastMessage("You have successfully disable Maker Checker.", toastType.success)

		getSortedData()
		dataTableRef?.current?.setEditMode(false);
		hideDisableMakerCheckerModal();

		await dispatch(syncProfile())
		setSubmitLoading(false)
	}

	async function handleDeleteSelected() {
		showDeleteModal()
	}

	function processEditedUsers(updatedUsers) {
		for (let userId in updatedUsers) {
			if (!updatedUsers[userId].role) {
				/** pass existing user roles */
				updatedUsers[userId].role = itemsUserManagement.find((user) => user.id === userId)?.role.map((r) => r.name);
			}

		}

	}

	async function editUsers(changedValues) {
		setSubmitLoading(true)

		const groupNameToId = ((isBuildex && isUserAccessManagementPhase2Enabled)) ? new Map((orgGroupsData || []).map(g => [g.name, g.id])) : null;
		const payload = []
		const userGroupPayload = []
		for (let userId in changedValues) {

			const updateValues = {
				id: userId,
			}

			processEditedUsers(changedValues)

			if (changedValues?.[userId]?.makerCheckerRole) {
				updateValues.makerCheckerRole = changedValues[userId].makerCheckerRole
			}
			if (changedValues?.[userId]?.role) {
				updateValues.roles = changedValues[userId].role
			}

			if (isBuildex && isUserAccessManagementPhase2Enabled && orgGroupsData?.length && changedValues?.[userId]?.groups) {
				userGroupPayload.push({
					userId,
					groupIds: (changedValues[userId].groups || []).map(g => groupNameToId?.get(g)).filter(Boolean)
				})
			}

			if (Object.keys(updateValues).length > 1) {
				payload.push(updateValues)
			}
		}

    try {
      if (isBuildex && isUserAccessManagementPhase2Enabled && orgGroupsData?.length && userGroupPayload.length) {
        const [error] = await groupApi.assignUser(userGroupPayload,organizationId);

        if (error) {
          return addToastMessage(error?.message, toastType.error)
        }
      }

      if (payload.length) {
        const [error, result] = await api.edits(payload);

        if (error) {
          return addToastMessage(error?.message, toastType.error)
        }

        const { failed, success } = result
        const [failedCount, successCount] = [(failed?.length ?? 0), success?.length ?? 0]

        if (failedCount > 0 && successCount > 0) {
          addToastMessage(`${successCount} users have been updated successfully. ${failedCount} users failed to update.`, toastType.error)
        } else if (failedCount === 0 && successCount > 0) {
          addToastMessage("You have successfully save the changes.", toastType.success)
        } else if (failedCount > 0 && successCount === 0) {
          addToastMessage("Failed to save the changes.", toastType.error)
        }
      }
    } catch (e) {
      addToastMessage(e?.message, toastType.error)
    } finally {
      getSortedData()
      setSubmitLoading(false)
      dataTableRef?.current?.setEditMode(false)
    }
  }

	/*=================================================
		API
	/================================================*/

	const { toastList, addToastMessage } = useToast()

	const { itemsUserManagement, fetchUserManagement, hasAtLeastOneMakerCheckerRole, getPendingMakerCheckerTransactions, loadingUserManagement, metaUserManagement, errorUserManagement, assignSelectedUsersToGroups } = useUserManagement();
	const { getAuthorizationURL, authLoginData } = useAuthLogin();

	const { itemsRole, fetchRole, errorRole } = useRole();

	const { register, handleSubmit, formState: { errors, isValid } } = useForm({
		mode: 'onChange'
	})

	const debouncedFetchSortedData = useDebounce(getSortedData, "dataFiltering");

	useEffect(() => {
		fetchRole(organizationId || null);
		if (organizationId && location) {
			setBreadcrumbsItems([
				{
					label: '',
					link: `/`,
				},
				{
					label: 'Participant',
					link: `/admin/participants`
				},
				{
					label: location.name,
					link: `/admin/participants/dashboard`,
					state: { data: location }
				},
				{
					label: pageTitle,
					link: ``
				}
			])
		}
	}, []);

	useEffect(() => {
		getAuthorizationURL({ redirectUrl: redirectURL })
	}, [])

	useEffect(() => {
		if (authLoginData) {
			checkUserExistsToExxonMobil()
		}
	}, [authLoginData, itemsUserManagement])

	const [userRole, setUserRole] = useState([]);
	const [inputUserRole, setInputUserRole] = useState([]);
	const [makerCheckerRole, setMakerCheckerRole] = useState([]);


	useEffect(() => {
		setUserRole(itemsRole)
		const filteredRoles = hideEtrRoleForOtherDex(itemsRole, isTradex)
		setInputUserRole(filteredRoles)
	}, [itemsRole])

	useEffect(() => {
		addToastMessage(errorUserManagement, toastType.error)
	}, [errorUserManagement])

	useEffect(() => {
		addToastMessage(errorRole, toastType.error)
	}, [errorRole])

	useEffect(() => {
		addToastMessage(errorInput, toastType.error)
	}, [errorInput])

	function checkUserExistsToExxonMobil() {
		if (!(profile.role.some((current) => current?.name?.toLowerCase() === "sgtradex admin")) && (authLoginData?.domains && authLoginData?.domains.length > 0) && (itemsUserManagement && itemsUserManagement.length > 0)) {
			const isObjectPresent = itemsUserManagement.find((o) => validateDomain(o.email, authLoginData?.domains));
			if (isObjectPresent) {
			}

		}
	}

	/*=================================================
		Maker-Checker
	/================================================*/
	const [makerCheckerToggle, setMakerCheckerToggle] = useState(false)
	const [noPendingMakerCheckerTranasction, setNoPendingMakerCheckerTransaction] = useState(false)
	const [isSaveChangesBtnDisabled, setIsSaveChangesBtnDisabled] = useState(false)
	const [atLeastOneUserMakerChecker, setAtLeastOneUserMakerChecker] = useState(organisationHasEnabledMakerChecker ?? false)
	const [syncChangedValues, setSyncChangedValues] = useState({})
	const [prevChangedValues, setPrevChangedValues] = useState({})
	const [syncSelectedRow, setSyncSelectedRow] = useState([])
	const [tableItems, setTableItems] = useState(itemsUserManagement);
	const dataTableRef = useRef(null);

	const roleTransformationStrats = {
		true: (role) => role ?? null,
		false: (role) => role ? upperFirst(toLower(role)) : "None"
	}

	const roleTransformStrats = (role, editMode) => {
		const roleTransformFunc = roleTransformationStrats[editMode];
		return roleTransformFunc(role);
	}

	useEffect(() => {
		const transformedItems = itemsUserManagement.map((item) => ({
			...item,
			makerCheckerRole: roleTransformStrats(item?.makerCheckerRole, dataTableRef?.current?.editMode || makerCheckerToggle)
		}));
		setTableItems(transformedItems);
	}, [dataTableRef?.current?.editMode, itemsUserManagement, makerCheckerToggle]);

	useEffect(() => {
		if (Object.keys(syncChangedValues).length === 0) {
			return
		}

		for (let userId in syncChangedValues) {
			if (
				syncChangedValues[userId]?.role
				&& syncChangedValues[userId]?.role !== prevChangedValues?.previous?.[userId]?.role
				&& JSON.stringify(syncChangedValues[userId]?.role?.sort()) !== JSON.stringify(prevChangedValues?.previous?.[userId]?.role?.sort())
			) {
				dataTableRef?.current?.updateData(userId, 'makerCheckerRole', 'NONE')
			}
		}

		if (organisationHasEnabledMakerChecker || makerCheckerToggle) {
			// check if object value is empty
			const hasEmptyValues = Object.values(syncChangedValues).every((v) => Object.keys(v).length === 0)

			checkHasAtLeastOneMakerCheckerRole(
				orgId,
				hasEmptyValues ? {} : syncChangedValues,
				setAtLeastOneUserMakerChecker
			)
		}

		setPrevChangedValues({
			previous: cloneDeep(prevChangedValues?.current),
			current: syncChangedValues
		})
	}, [syncChangedValues])

	async function checkHasAtLeastOneMakerCheckerRole(orgId, changedValues, setAtLeastOneUserMakerChecker) {
		let makerCheckerCount = {};
		let isMakerCheckerEnabled = false
		if (organisationHasEnabledMakerChecker) {
			const changeMakerCheckerRole = {}
			for (let userId in changedValues) {
				if (changedValues[userId]?.makerCheckerRole) {
					changeMakerCheckerRole[userId] = {
						makerCheckerRole: changedValues[userId].makerCheckerRole
					}
				}
			}

			makerCheckerCount = await hasAtLeastOneMakerCheckerRole(orgId, changeMakerCheckerRole);
			isMakerCheckerEnabled = true
		} else {
			Object.values(changedValues).forEach((u) => {
				if (u?.makerCheckerRole) {
					makerCheckerCount[u.makerCheckerRole] = (makerCheckerCount[u.makerCheckerRole] ?? 0) + 1;
					isMakerCheckerEnabled = true
				}
			})
		}

		if (isMakerCheckerEnabled) {
			setIsSaveChangesBtnDisabled(!(makerCheckerCount?.['MAKER'] > 0 && makerCheckerCount?.['CHECKER'] > 0))
		}

		setAtLeastOneUserMakerChecker(makerCheckerCount?.['MAKER'] > 0 && makerCheckerCount?.['CHECKER'] > 0);
	}

	async function checkHasPendingMakerCheckerTransactions(orgId) {
		return (organisationHasEnabledMakerChecker && (await getPendingMakerCheckerTransactions(orgId))?.count > 0) ?? true
	}

	async function handledMakerCheckerToggle(checked) {
		if (checked) {
			showVerifyMakerCheckerModal()
		} else {
			const status = await checkHasPendingMakerCheckerTransactions(orgId);
			setNoPendingMakerCheckerTransaction(!status)
			showDisableMakerCheckerModal()
		}
	}

	function acceptVerifyMakerCheckerModal() {
		setMakerCheckerToggle(true)
		hideVerifyMakerCheckerModal();
	}

	function acceptDisableMakerCheckerModal() {
		handleDisableMakerChecker();
	}

	function handleCancel() {
		setMakerCheckerToggle(false);
		setIsSaveChangesBtnDisabled(false)
		if (organisationHasEnabledMakerChecker) {
			setAtLeastOneUserMakerChecker(true);
		} else {
			setAtLeastOneUserMakerChecker(false);
		}
	}

	function handleSave() {
		setMakerCheckerToggle(false);
	}

	function handleEditSave(changedValues) {
		if (makerCheckerToggle) {
			handleEnableMakerChecker(changedValues)
		} else {
			editUsers(changedValues);
		}
	}

	/*=================================================
		Sort
	/================================================*/
	function getSortedData() {
		fetchUserManagement({
			currentPage: currentPage,
			page: currentPage,
			count: pageLimit,
			pageSize: pageLimit,
			orgId: organizationId,
			start:
				startDate !== null
					? moment(startDate).format("yyyy-MM-DD HH:mm")
					: null,
			end:
				endDate !== null
					? moment(endDate).format("yyyy-MM-DD HH:mm")
					: null,
			sortBy: sortConfig?.sortBy ?? null,
			direction: sortConfig?.direction === true ? "asc" : "desc",
			role: convertArrayToString(userRole) ?? null,
			email: searchUser,
			makerCheckerRole: makerCheckerRole?.map(s => s.value)?.filter(s => s && s !== 'NONE')?.join(',') || null,
			groupIds: Array.isArray(filter.groups) ? filter.groups.map(g => g.id).filter(Boolean).join(',') : null,
		});
	}


	/*=================================================
		Pagination
	/================================================*/

	const [currentPage, setCurrentPage] = useState(1)

	useEffect(() => {
		if (!loadingUserManagement)
			getSortedData()
	}, [currentPage, startDate, endDate, userRole, makerCheckerRole, sortConfig, pageLimit])

	useEffect(() => {
		if (searchUser !== null && searchUser !== undefined) {
			setCurrentPage(1);
			debouncedFetchSortedData()
		}
	}, [searchUser])

	useEffect(() => {
		if (currentPage > metaUserManagement?.totalPages) {
			setCurrentPage(metaUserManagement?.totalPages || 1)
		}
	}, [itemsUserManagement])

	function convertArrayToString(array) {
		if (!Array.isArray(array)) {
			return null;
		}

		if (array.every((item) => item.$selected === true)) {
			return null;
		}

		const tempArray = retrieveId(array);
		if (tempArray.length > 0) {
			return tempArray.toString();
		}

		return null;
	}

	function retrieveId(array) {
		const tempArray = [];

		array.forEach((item) => {
			if (item.$selected) {
				tempArray.push(item.label);
			}
		});

		return tempArray;
	}

	function addModalPopupMessage(error) {
		setErrorUserMessage(error.message);
		showErrorUserModal();
	}

	/*=================================================
		Delete
	/================================================*/

	async function deleteUser(item) {
		if (!item) {
			const [error] = await api.deletes(syncSelectedRow);

			if (error) {
				if (isSoftDeleteUserEnabled && error?.message.includes("Please assign another one before deletion.")) {
					return addModalPopupMessage(error);
				}
				return addToastMessage(error?.message, toastType.error)
			}

			addToastMessage(`You have removed user(s) successfully`, toastType.success)
			dataTableRef?.current?.setRowSelection({})
		} else {
			const [error] = await api.delete(item?.id);

			if (error) {
				if (isSoftDeleteUserEnabled && error?.message.includes("Please assign another one before deletion.")) {
					return addModalPopupMessage(error);
				}
				return addToastMessage(error?.message, toastType.error)
			}

			addToastMessage(`You have removed ${item?.email} successfully`, toastType.success)
			setDeleteItem(null)
		}

		getSortedData()
		// getUsersLists()
		// checkUserExistsToExxonMobil()
	}

	/*=================================================
		Create
	/================================================*/


	const handleInputSubmitClick = async (input) => {
		if (!hasRequiredInputValues(input)) return;

		if (!validateEmail(input?.email)) {
			return setErrorInput("Please enter a valid Email Address")
		}
		if (!validEmailDomain(input?.email, dex)) {
			return setErrorInput("The following domain are not allowed to be Registered")
		}
		if (submitLoading) {
			return
		}

		setSubmitLoading(true)

		let user = [{
			email: input.email,
			roles: input.role,
			makerCheckerRole: input.makerCheckerRole?.toUpperCase() ?? null,
			organizationId: organizationId ? organizationId : null
		}]
		const serializedUsers = user.map(u => {
			const serializedRoles = u.roles.reduce((labels, role) => {
				if (role.$selected) {
					labels.push(role.label);
				}
				return labels
			}, []);

			if (u.organizationId) {
				return {
					email: u.email.toLowerCase(),
					roles: serializedRoles,
					makerCheckerRole: u.makerCheckerRole,
					organizationId: u.organizationId
				}
			} else {
				return {
					email: u.email.toLowerCase(),
					roles: serializedRoles,
					makerCheckerRole: u.makerCheckerRole,
				}
			}
		});

		const [error, createResponse] = await api.create(serializedUsers)

		if (error) {
			setSubmitLoading(false)
			return addToastMessage("Failed to add users", toastType.error)
		}

		if (isBuildex && isUserAccessManagementPhase2Enabled && orgGroupsData?.length && chipValuesForCreate?.length) {
			try {
				const createdUsers = createResponse.users || []
				const assignments = createdUsers.map(u => ({ userId: u.id, groupIds: chipValuesForCreate?.map(g => g.id) || [] }))
				const [error] = await groupApi.assignUser(assignments,organizationId)

				if (error) {
					return addToastMessage(error?.message, toastType.error)
				}

				setChipValuesForCreate([]);
			} catch (e) {
				return addToastMessage(e?.message, toastType.error)
			} finally {
				setSubmitLoading(false)
			}
		}

		setSubmitLoading(false)
		addToastMessage("You have successfully added new user account.", toastType.success)
		setInputValues({});
		setInputUserRole(hideEtrRoleForOtherDex(itemsRole, isTradex).map(s => ({ ...s, $selected: false })))
		getSortedData();
	}

	const handleInputCancelClick = async () => {
		setInputValues({});
		setInputUserRole(hideEtrRoleForOtherDex(itemsRole, isTradex).map(s => ({ ...s, $selected: false })))
	}

	/*=================================================
		Edit
	/================================================*/

	useEffect(() => {
		const add = editUserRole?.map(item => {
			if (item.userId === undefined && item.$selected === true) {
				return item.label
			} return null
		}).filter(item => item)
		setAddedRole(add)

		const remove = editUserRole?.map(item => {
			if (item.userId !== undefined && item.$selected === false) {
				return item.label
			} return null
		}).filter(item => item)
		setRemovedRole(remove)
	}, [editUserRole])


	const handleUpdateRoleSelect = (item) => {

		const index = editUserRole?.findIndex(role => role.id === item.id);
		const updatedItems = produce(editUserRole, draft => {
			draft[index].$selected = !draft[index].$selected
		})

		setEditUserRole(updatedItems)
	}

	const Checkbox = ({ selected, handleUpdateItemSelect, disabled }) => {
		const checkBoxClass = classname({
			"use-case-request__checkbox": true,
			"use-case-request__checkbox--active": selected,
			"use-case-request__checkbox--disabled": disabled,
		})

		return (
			<div data-testid="role-checkbox" className={checkBoxClass} onClick={handleUpdateItemSelect} onKeyDown={handleUpdateItemSelect}>{selected ? <IconTick /> : null}</div>
		)
	}

	async function editUser() {

		const roleItems = editUserRole?.filter(role => role.$selected === true).map(role => {
			return role.label
		})

		const payload = {
			"roles": roleItems
		}

		const [error] = await api.edit(editItem?.id, payload);

		if (error) {
			return addToastMessage(error?.message, toastType.error)
		}

		setEditItem({})
		addToastMessage("You have successfully edit the user information.", toastType.success)

		getSortedData()
	}

	/*=================================================
		Data Table
	/================================================*/


	const [filter, setFilter] = useState({});
	const [inputValues, setInputValues] = useState({});

	useEffect(() => {
		if (makerCheckerToggle) {
			dataTableRef?.current?.setEditMode(true)
		}
	}, [makerCheckerToggle])

	const onInputChange = React.useCallback((inputKey) => {
		return (e) => {
			if (inputKey === 'email') {
				setInputValues({
					...inputValues,
					[inputKey]: e.target.value
				})
			} else if (inputKey === 'role') {
				setInputUserRole(e)
				setInputValues({
					...inputValues,
					[inputKey]: e.filter(s => s.$selected)
				})
			} else if (inputKey === 'makerCheckerRole') {
				setInputValues({
					...inputValues,
					[inputKey]: e
				})
			}
			return e;
		}
	}, [inputValues]);

	function hasRequiredInputValues(inputValues) {
		return columns.every((column) => {
			if (column.input?.required) {
				const inputValue = inputValues[column.id];
				return Array.isArray(inputValue) ? inputValue.length > 0 : !!inputValue;
			}
			return true;
		});
	}

	const inputMakerCheckerRoleOptions = [
		{ id: 'MAKER', name: 'Maker', value: 'MAKER' },
		{ id: 'CHECKER', name: 'Checker', value: 'CHECKER' },
		{ id: 'NONE', name: 'None', value: 'NONE' },
	]

	function handleMakerCheckerRolePermission() {
		const role = inputUserRole.filter(s => (s.$selected === true)).map(s => { return s.label })

		const conditionalOption = uniq(role.reduce((prev, curr) => {
			prev = prev.concat(makerCheckerRolePermission[curr] ?? [])
			return prev
		}, [])).sort().map(s => inputMakerCheckerRoleOptions.find(o => o.value === s))

		return conditionalOption;
	}

	const defaultColumns = [
		{
			id: 'select',
			size: 55,
			maxSize: 55,
			enableResizing: false,
			header: SelectAllHeader,
			cell: SelectRowCell,
			input: {
				type: 'text',
				render: () => (
					<div className="user-management-input-icon" style={{ width: "100%", padding: '0px' }}>
						<IconPlus />
					</div>
				),
			},
		},
		// {
		// 	id: 'expander',
		// 	header: () => null,
		// 	cell: ({ row }) => {
		// 		return row.getCanExpand() ? (
		// 			<button
		// 				{...{
		// 					onClick: row.getToggleExpandedHandler(),
		// 					style: { cursor: 'pointer' },
		// 				}}
		// 			>
		// 				{row.getIsExpanded() ? '👇' : '👉'}
		// 			</button>
		// 		) : (
		// 			'🔵'
		// 		)
		// 	},
		// },
		{
			id: "email",
			header: 'User Email',
			accessorKey: 'email',
			minSize: 300,
			flex: 0.3,
			editable: false,
			sortable: true,
			enableResizing: false,
			meta: {
				style: {
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					display: '-webkit-box',
					WebkitLineClamp: 2,
					WebkitBoxOrient: 'vertical',
					textWrap: 'wrap',
				}
			},
			input: {
				type: 'text',
				required: true,
				render: () => (
					<div style={{ width: "100%", padding: '10px 4px' }}>
						<Input
							addClass="user-management__create-input"
							placeholder="Enter New User Email"
							fullWidth
							type='text'
							value={inputValues?.['email'] ?? ''}
							onChange={onInputChange('email')}
						/>
					</div>
				),
			},
		},
		{
			id: "role",
			header: 'User Role',
			minSize: 250,
			flex: (organisationHasEnabledMakerChecker ? 0.3 : 0.4),
			editable: true,
			enableResizing: false,
			meta: {
				style: {
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					display: '-webkit-box',
					WebkitLineClamp: 2,
					WebkitBoxOrient: 'vertical',
					textWrap: 'wrap',
				}
			},
			accessorKey: 'role',
			accessorFn: row => {
				return row.role.map(role => role.name).sort().join(', ')
			},
			edit: {
				type: 'multiSelect',
				options: hideEtrRoleForOtherDex(itemsRole, isTradex),
				optionKey: 'id',
				optionValue: 'label',
				optionSelected: 'label',
			},
			input: {
				type: 'text',
				required: true,
				render: () => (
					<div style={{ width: "100%", padding: '10px 4px' }}>
						<Select
							multiple
							placeholder="Select User Role"
							maxTagCount="responsive"
							notFoundContent="This is empty"
							data-testid="user-management-create-role"
							popupMatchSelectWidth={false}
							value={getSelectedValues(inputUserRole)}
							onChange={(values) => onInputChange('role')(applySelectedValues(inputUserRole, values))}
						>
							<SelectContent>{renderSelectItems(inputUserRole)}</SelectContent>
						</Select>
					</div>
				),
			},
		},
		...((isBuildex && isUserAccessManagementPhase2Enabled && orgGroupsData?.length) ? [{
			id: "groups",
			header: 'Groups',
			minSize: 250,
			flex: (organisationHasEnabledMakerChecker ? 0.3 : 0.4),
			editable: true,
			sortable: false,
			enableResizing: false,
			meta: {
				style: {
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					display: '-webkit-box',
					WebkitLineClamp: 2,
					WebkitBoxOrient: 'vertical',
					textWrap: 'wrap',
				}
			},
			accessorFn: row => {
				const groupIds = row.userGroupIds;
				if (!groupIds) return "";
			
				const names = groupIds
					.map(id => orgGroupsData?.find(g => g.id === id)?.name)
					.filter((name) => Boolean(name));
			
				return names.join(", ");
			},
			edit: {
				type: 'multiSelect-with-search',
				options: transformedGroups,
				optionKey: 'id',
				optionValue: 'name',
				optionSelected: 'name',
				showOptionTooltip: true,
			},
			input: {
				type: 'text',
				required: false,
				render: () => (
					<div className="user-management__create-multi-select-group">
						<InputDropdownDS
							onChange={noop}
							name={'groupSearch'}
							dropdownOption={transformedGroups}
							placeholder={"Select Group"}
							onSelectDropdownItem={setChipValuesForCreate}
							dropdownSelectedOption={chipValuesForCreate}
							multipleSelection={true}
							fullWidth={true}
							showTooltipInfo={true}
						/>
					</div>
				),
			},
		}] : []),
		{
			id: "makerCheckerRole",
			header: 'Maker / Checker',
			minSize: 200,
			flex: 0.2,
			accessorKey: 'makerCheckerRole',
			editable: true,
			sortable: true,
			enableResizing: false,
			editFn: row => {
				let role = syncChangedValues?.[row.id]?.role ?? row?.role?.map(s => s?.name) ?? [];
				role = Array.isArray(role) ? role : role.split(',').map(s => s.trim())

				const conditionalOption = uniq(role.reduce((prev, curr) => {
					prev = prev.concat(makerCheckerRolePermission[curr] ?? [])
					return prev
				}, [])).sort().map(s => inputMakerCheckerRoleOptions.find(o => o.value === s))

				return {
					type: 'singleSelect',
					options: conditionalOption,
					optionKey: 'id',
					optionValue: 'name',
					optionSelected: 'value',
				}
			},
			input: {
				type: 'text',
				required: true,
				render: () => (
					<div style={{ width: "100%", padding: '10px 4px' }}>
						<Select
							placeholder="Select Maker / Checker"
							data-testid="user-management-create-maker-checker"
							value={selectedOptionValue(handleMakerCheckerRolePermission(), inputValues?.['makerCheckerRole'])}
							onChange={onInputChange('makerCheckerRole')}
						>
							<SelectContent>{renderValueOptions(handleMakerCheckerRolePermission())}</SelectContent>
						</Select>
					</div>
				),
			},
		},
		{
			id: "created",
			header: 'Date Created',
			minSize: 100,
			flex: (organisationHasEnabledMakerChecker ? 0.2 : 0.4),
			sortable: true,
			enableResizing: false,
			accessorFn: row => {
				let date = moment(row.createdAt);
				return date.isValid() ? date.format("D/M/YYYY") : ""
			},
		},
		{
			id: "action",
			header: "",
			size: 120,
			maxSize: 120,
			sortable: false,
			enableResizing: false,
			meta: {
				style: {
					textAlign: 'right',
					marginLeft: 'auto',
					marginRight: 'auto'
				}
			},
			accessorFn: row => {
				const isSelf = row?.id === profile?.id;
				return <div>
					{(isSoftDeleteUserEnabled && !dataTableRef?.current?.editMode) ? <DropdownMenu placement="bottomRight">
						<DropdownMenuTrigger asChild>
							<ButtonDS
								variant={ButtonVariant.UTILITY}
								type={ButtonType.UTILITY}
								dataTestId="user-row-actions"
								className="account-settings__modal_term-list__actions"
								text={
									<Icon
										name="ellipsis-vertical"
										size={IconSize.MEDIUM}
										style={IconStyle.SOLID}
									/>
								}
							/>
						</DropdownMenuTrigger>
						<DropdownMenuContent>
							<DropdownMenuItem
								itemKey="deleteAccount"
								disabled={isSelf}
								onSelect={() => {
									setDeleteItem(row);
									showDeleteModal();
								}}
							>
								{isSelf ? "Delete Account (unavailable - cannot delete self)" : "Delete Account"}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu> : <Button
						addClass="user-management__action_delete"
						ghost
						noborder
						round
						sizing="circle"
						prefix={<DeleteIcon />}
						submit={() => {
							setDeleteItem(row)
							showDeleteModal()
						}}
					/>}
				</div>
			},
			input: {
				type: 'actions',
				render: () => {
					if (Object.values(inputValues)?.filter(Boolean)?.length === 0) {
						return <></>
					}
					return (
						<div className="data_table__input__action">
							<div className="data_table__input__action__left">
								<div className="data_table__input__action__divider" />
								<div
									data-testid="data_table__input-cancel-add-user"
									className="data_table__input__action__button"
									onClick={handleInputCancelClick}
								>
									<IconClose />
								</div>
								<div
									data-testid="data_table__input-submit-add-user"
									className={`data_table__input__action__button submit ${!hasRequiredInputValues(inputValues) && 'data_table__input__action__button--disabled'}`}
									onClick={() => handleInputSubmitClick(inputValues)}
								>
									<IconTickCircle />
								</div>
							</div>
						</div >
					)
				}
			},
		}
	];
	const defaultFilterOptions = [
		{
			key: 'userRole',
			type: 'multiSelect',
			label: 'User Role',
			placeHolderText: 'Select User Role',
			options: []
			//  userRole.map(s => ({ ...s, value: s.label.toUpperCase(), name: s.label })),
		},
		...((isBuildex && isUserAccessManagementPhase2Enabled && orgGroupsData?.length) ? [{
			key: 'groups',
			type: 'multiSelect',
			label: 'Groups',
			placeHolderText: 'Select Groups',
			options: orgGroupsData.map(({ id, name }) => ({ id, name, value: name })),
		}] : []),
		{
			key: 'makerCheckerRole',
			type: 'multiSelect',
			label: 'Maker / Checker',
			placeHolderText: 'Select Maker / Checker',
			options: inputMakerCheckerRoleOptions,
		},
		{
			key: 'date',
			type: 'dateRange',
			label: 'Date Created',
			placeHolderText: ['Start Date', 'End Date'],
		}
	]

	const columns = useMemo(() => {
		if (organisationHasEnabledMakerChecker) {
			return defaultColumns
		}
		else if (makerCheckerToggle) {
			return defaultColumns.map(column => {
				column.editable = (column.id === 'makerCheckerRole')
				return column
			})
		}
		else {
			return defaultColumns.filter(column => column.id !== 'makerCheckerRole')
		}
	}, [makerCheckerToggle, organisationHasEnabledMakerChecker, userRole, inputMakerCheckerRoleOptions, orgGroupsData])

	const filterOptions = useMemo(() => {
		return organisationHasEnabledMakerChecker ?
			defaultFilterOptions :
			defaultFilterOptions.filter(option => option.key !== 'makerCheckerRole')
	}, [organisationHasEnabledMakerChecker, userRole, inputMakerCheckerRoleOptions, orgGroupsData])

	useEffect(() => {
		setStartDate(filter?.startDate ?? null);
		setEndDate(filter?.endDate ?? null);
		if (filter?.userRole > 0 || userRole?.length > 0) {
			const updatedItems = produce(userRole, draft => {
				draft.map(s => s.$selected = false);
				if (filter?.userRole) {
					filter?.userRole.forEach(role => {
						const index = userRole?.findIndex(s => s.label === role?.label);
						draft[index].$selected = true;
					});
				}
			});
			setUserRole(updatedItems);
		}
		setSearchUser(filter?.search ?? null);
		setMakerCheckerRole(filter?.makerCheckerRole ?? null);
		setCurrentPage(1);
		// getSortedData()
	}, [filter]);

	/*=================================================
		Navigate
	/================================================*/

	/*=================================================
		Two-Factor Authentication
	/================================================*/

	/*=================================================
		Two-Factor Authentication Steps
	/================================================*/

	const TfaSteps = () => {
		return <>
			<div className="tfa-modal__steps">
				<div className="tfa-modal__item">
					<div className="tfa-modal__item__number"><strong>1</strong></div>
					<div className="tfa-modal__item__image" id="icon-phone"><IconSmartPhone /></div>
					<div className="tfa-modal__item__text">
						<span>Step 1</span><br />
						Get the Google Authenticator or Microsoft Authenticator App from:<br />
						Google Authenticator – <a href={googleStoreAuthenticatorLink} rel="noreferrer" target="_blank">Google Play Store</a> or <a href={ituneStoreAuthenticatorLink} rel="noreferrer" target="_blank">iTunes App Store</a><br />
						Microsoft Authenticator – <a href={googleStoreMicrosoftAuthenticatorLink} rel="noreferrer" target="_blank">Google Play Store</a> or <a href={ituneStoreMicrosoftAuthenticatorLink} rel="noreferrer" target="_blank">iTunes App Store</a>
					</div>
				</div>
				<div className="tfa-modal__item">
					<div className="tfa-modal__item__number"><strong>2</strong></div>
					<div className="tfa-modal__item__image" id="icon-qr"><IconQr /></div>
					<div className="tfa-modal__item__text">
						<span>Step 2</span><br />
						Choose Scan the QR Code appears at the time of Login
					</div>
				</div>
				<div className="tfa-modal__item">
					<div className="tfa-modal__item__number"><strong>3</strong></div>
					<div className="tfa-modal__item__image" id="icon-lock"><IconPadLock /></div>
					<div className="tfa-modal__item__text">
						<span>Step 3</span><br />
						Set up Authenticator.
						Enter the six-digit code that you see in the app
					</div>
				</div>
			</div>
		</>
	}

	/*=================================================
		API Action Functions
	/================================================*/

	async function tfaVerifierHanlder(data) {
		if (submitLoading) {
			return
		}

		const payload = {
			otp: data.otp
		}
		hideVerifyTfa()

		setSubmitLoading(true)

		const [error] = await TwoFactorAuthenticationApi.deactivateTfa(payload)

		setSubmitLoading(false)


		if (error) {
			return addToastMessage(error.message ?? "Failed to deactivate two-factor authentication.", toastType.error)
		}

		await dispatch(syncProfile())

		addToastMessage("You have disabled Two Factor Authentication for all the users.", toastType.success)

		setTimeout(function () {
			window.location.reload();
		}, 3000);
	}

	/*=================================================
		Other Functions
	/================================================*/

	async function handleConfirmation() {
		hideEnableTfa()
		setSubmitLoading(true)

		const [error] = await TwoFactorAuthenticationApi.activateTfa()

		setSubmitLoading(false)
		if (error) {
			return addToastMessage(error, toastType.error)
		}

		showSuccessfulTfa();
	}


	function WarningBarMakerChecker({ text }) {
		return (
			<div className="maker-checker_feature-banner">
				<div className="layout-maker-checker__block">
					<div className="maker-checker_warning-bar">
						<div className="maker-checker_warning-icon"><ExclamationCircle /></div>
						<div className="maker-checker_warning-content">
							{
								text ? (text) : (
									<>
										Appoint at least one user as the maker and one user as the checker from User Management List.
										{/* <div>Note: For E-Transferable Record feature, add at least two users to the ETR role.</div> */}
									</>
								)
							}
						</div>
					</div>
				</div>
			</div>
		);
	}

	const assignSelectedUsersToTheSelectedGroups = async () => {
		setSubmitLoading(true);
	
		const result = await assignSelectedUsersToGroups(syncSelectedRow, chipValues, itemsUserManagement, addToastMessage, toastType, organizationId);
	
		if (result.success) {
			setChipValues([]);
			dataTableRef?.current?.setRowSelection({});
			getSortedData();
			hideAssignGroupModal();
		}
		
		setSubmitLoading(false);
	};

	const isEditable = isBuildex && isUserAccessManagementPhase2Enabled && orgGroupsData?.length ? syncSelectedRow.length === 0 : true;

	return (
		<>
			{/* [DS-MIGRATION] legacy Breadcrumbs - replaced by DS-backed Breadcrumb. Remove in follow-up PR. */}
			{/* <Breadcrumbs breadcrumbItems={breadcrumbsItems} /> */}
			<Breadcrumb items={breadcrumbsItems} />
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{organizationId && location
							? `All Users of ${location.name}`
							: pageTitle}
					</div>
				</div>
				<Box display="flex" justify="end">
				</Box>
			</div>

			<hr />

			{
				(role !== 'admin' && (!makerCheckerToggle || (organisationHasEnabledMakerChecker))) && !(dataTableRef?.current?.editMode || false) && (
					<div className="maker-checker_feature-banner">
						<div className="layout-maker-checker__block">
							{
								(
									<>
										<div className="layout-maker-checker__header">
											<div className="layout-maker-checker__header__text">
												Security Features
											</div>
										</div>
										<div className="layout-maker-checker__feature">
											<div className="layout-maker-checker__feature-title">
												<div>Enable additional security using Two Factor Authentication for all the users.</div>
												<div
													data-testid="user-management-tfa-learn-more"
													title="Learn More"
													className="layout-maker-checker__feature-learn-more"
													tabIndex={0}
													onClick={() => {
														showLearnMoreTfa()
													}}
													onKeyDown={() => {
														showLearnMoreTfa()
													}}
												>
													<span>Learn More</span>
												</div>
											</div>
											<div className="layout-maker-checker__feature-toggle">
												<div>{checkProfile ? "Enabled" : "Disabled"}</div>
												<ToggleSwitch3
													id="enableTfa"
													checked={checkProfile}
													onChange={handledEnableTfa}
													data-testid="enable-Tfa-toggle"
												/>
											</div>
										</div>
										{
											isTradex &&
											<div className="layout-maker-checker__feature">
												<div className="layout-maker-checker__feature-title">
													<div>Enable approval workflow for Data Element Sharing and E-transferable Records.</div>
													<div title="Learn More" className="layout-maker-checker__feature-learn-more"
														tabIndex={0}
														onClick={() => {
															showLearnMoreMekerCheckerModal()
														}}
														onKeyDown={() => {
															showLearnMoreMekerCheckerModal()
														}}
													>
														<span>Learn More</span>
													</div>
												</div>
												<div className="layout-maker-checker__feature-toggle">
													<div>{organisationHasEnabledMakerChecker ? "Enabled" : "Disabled"}</div>
													<ToggleSwitch3
														id="makerCheckerToggle"
														checked={organisationHasEnabledMakerChecker}
														onChange={handledMakerCheckerToggle}
														data-testid="enable-maker-checker-toggle"
													/>
												</div>
											</div>
										}
									</>
								)
							}
						</div>
					</div>
				)
			}
			{
				(
					(makerCheckerToggle && !atLeastOneUserMakerChecker) ||
					(organisationHasEnabledMakerChecker && !atLeastOneUserMakerChecker)
				) && (
					<WarningBarMakerChecker />
				)
			}

			<div className="overview">

				<Box minH="540px" mt={4}>
					{(
						<DataTable3
							ref={dataTableRef}
							data={tableItems}
							columns={columns}
							isLoading={loadingUserManagement}
							showPageSize

							showInput={!makerCheckerToggle && !dataTableRef?.current?.editMode}

							showSelectedActions
							selectedHeaderActions={<div
								className="user-management__delete_icon__selected"
							>
								<Button
									ghost
									noborder
									round
									sizing="mini"
									prefix={<IconDelete />}
									text="Delete Selection"
									submit={handleDeleteSelected}
								/>
							</div>}

							showSelectedSecondaryActions={!isEditable}
							selectedSecondaryActions={
								<div className="user-management__selected-secondary-actions">
									<Button round text="Assign Group" submit={() => showAssignGroupModal()}/>
								</div>
							}
							isEditable={isEditable}
							onEditSave={handleEditSave}
							showHeader
							headerProps={{
								filter,
								setFilter,
								showSearchInput: true,
								filterOptions,
							}}
							onCancel={handleCancel}
							onSave={handleSave}

							meta={{
								pagination: {
									...metaUserManagement,
									setPageLimit,
									setCurrentPage,
								}
							}}

							sortConfig={sortConfig}
							setSortConfig={setSortConfig}

							setSyncChangedValues={setSyncChangedValues}
							setSyncSelectedRow={setSyncSelectedRow}

							saveChangesBtnDisabled={isSaveChangesBtnDisabled}
						/>
					)}
				</Box>
			</div>

			<Modal visible={isDeleteVisible} variant="small" close={hideDeleteModal}>
				<CardConfigure addHeaderClass={'maker-checker-modal__header'} >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div >
											<div className='maker-checker-modal__icon maker-checker-modal__disable'><IconTrash /></div>
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Delete User Account
										</div>

									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							{
								!isSoftDeleteUserEnabled ?
								<div className="maker-checker-modal__content">
									Are you sure you want to remove the this user(s)? This action cannot be revise
								</div> :
								<div className="maker-checker-modal__content">
									Are you sure you want to delete selected user account? Deleted user can no longer login to their account, but attributed historical records and audit logs will still be preserved.
								</div>
							}
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							{
								<div className='maker-checker-modal__confirmation_actions'>
									<Button ghost noborder round submit={() => hideDeleteModal()} text="Cancel" />
									<Button full round error maxWidth="100" text="Confirm" id="delete-user-confirm-button" submit={() => {
										setDeleteItem("");
										hideDeleteModal();
										deleteUser(deleteItem);
									}} />
								</div>
							}
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={isEditVisible} variant="medium" close={() => {
				hideEditModal()
				setAddedRole([])
				setRemovedRole([])
			}}>
				<CardConfigure  >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div >
								<div className="card-configure__header__left">
									<div className="card-configure__title">
										<div className="card-configure__title__left">
											<div>
												<IconProfile />
											</div>
										</div>
										<div className="card-configure__title__right">
											<div className="card-configure__title__right__top">
												Modify Selection
											</div>
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="card-configure__sub-header">
								<div className="user-mgt-edit__container">
									<div className="user-mgt-edit__email">
										<div className="user-mgt-edit__title">User Email Address</div>
										<div className="user-mgt-edit__email-input">
											<input type="email" id="email" placeholder={editItem?.email} disabled={true} />
										</div>
									</div>
								</div>
								<div className="user-mgt-edit__role-container">
									<div className="user-mgt-edit__title user-mgt__edit-role-title">Current Active Role(s)</div>
									{editItem?.role?.map((item, index) => {
										return <div key={index} className="user-mgt-edit__role-label">{item.name}</div>
									})}
								</div>
							</div>
							<div className="user-mgt-edit__role-selection-desc">Select or deselect roles to modify the role selection</div>
							<div className="user-mgt-edit__role-selectors-cols">
								{editUserRole?.map((item, index) => {
									return (
										<div className="user-mgt-edit__role-selectors-col" key={index}>
											<Checkbox data-testid='role-checkbox' selected={item.$selected} handleUpdateItemSelect={() => handleUpdateRoleSelect(item)} />
											<div className="user-mgt-edit__role-name">{item.label}</div>
										</div>
									)
								})}
							</div>
							{
								(addedRole.length > 0 || removedRole.length > 0) && (
									<div className="user-mgt-edit__role-selection-text">
										You
										{(addedRole.length > 0) && (
											<> selected new role "{addedRole.join(", ")}"</>
										)}
										{(addedRole.length > 0 && removedRole.length > 0) && (
											<> and</>
										)}
										{(removedRole.length > 0) && (
											<> deselected role "{removedRole.join(", ")}"</>
										)}
									</div>
								)
							}
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button
								text="Save Changes"
								submit={() => {
									hideEditModal();
									editUser();
									setAddedRole([])
									setRemovedRole([])
								}}
								full
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={enableTfa} variant="large" close={hideEnableTfa}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Enable Two Factor Authentication
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="tfa-modal__content">
								<div className="tfa-modal__header">
									<div className="tfa-modal__header__top">
										Are you sure you want to enable Two Factor Authentication for all the users?
									</div>
									<div className="tfa-modal__header__bottom">
										Total Number of Users <span>{metaUserManagement?.count}</span>
									</div>
								</div>
								<TfaSteps />
								<div className="tfa-modal__footer">
									<span>Note</span> : Email notification will be sent to all the participants.
								</div>
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button text="Confirm"
								id="enable-2fa-confirm-button"
								submit={() => {
									handleConfirmation()
								}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={successfulTfa} variant="medium" close={()=>{dispatch(logout(goTo))}}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
						<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
										Enable 2 Factor Authentication
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="tfa-modal__content">
								<div className="tfa-modal__header">
									<div className="tfa-modal__header__top">
										<IconTickEmptyCircle />
										<div className="tfa-modal__header__text">
											You have successfully enabled Two Factor Authentication
											for all the users of your organization.
											Logout to activate right now.
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button text="Logout Now"
								submit={() => {
									dispatch(logout(goTo))
								}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={learnMoreTfa} variant="medium" close={hideLearnMoreTfa}>
				<CardConfigure style={{width:'718px'}}>
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											<span className="tfa-text--error">About Two Factor Authentication</span>
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="tfa-modal__content">
								<div className="tfa-modal__header">
									<div className="tfa-modal__header__top">
										Two Factor Authentication is a security method in which users validate their identity using two independent authentication factors. 2FA is used to safeguard a user's credentials as well as the resources they have access to.
									</div>
									<div className="tfa-modal__header__bottom">
										How to use an Authenticator App
									</div>
								</div>
								<TfaSteps />
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button text="Okay, Got it!" ghost
								data-testid="close-button"
								submit={() => {
									hideLearnMoreTfa()
								}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={disableTfa} variant="medium" close={hideDisableTfa}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
						<div className="maker-checker-modal__header">
								<div className="card-configure__header__left">
									<div className="card-configure__title">
										<div className="card-configure__title__left">
										</div>
										<div className="card-configure__title__right">
											<div className="card-configure__title__right__top">
											<span className='inactive-title'>Disable Two Factor Authentication</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="tfa-modal__content">
								<div className="tfa-modal__header">
									<div className="tfa-modal__header__top">
										Are you sure you want to disable Two Factor Authentication for all the users?
									</div>
									<div className="tfa-modal__header__bottom">
										Total Number of Users <span>{metaUserManagement?.count}</span>
									</div>
								</div>
								<div className="tfa-modal__gap"></div>
								<div className="tfa-modal__footer">
									<span>Note</span> : Email notification will be sent to all the participants.
								</div>
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button text="Disable" error
								submit={() => {
									hideDisableTfa()
									showVerifyTfa()
								}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={verifyTfa} close={hideVerifyTfa}  variant="medium">
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
								<div className="card-configure__header__left">
									<div className="card-configure__title">
										<div className="card-configure__title__right">
											<div className="card-configure__title__right__top">
											<span className='inactive-title'>Disable Two Factor Authentication</span>
											</div>
										</div>
									</div>
								</div>
							</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="tfa-modal__content">
								<div className="tfa-modal__header">
									<div className="tfa-modal__header__top">
										Enter the Security Code using Google Authenticator or Microsoft Authenticator <br />
										Mobile App to confirm the action.
									</div>
									<div className="tfa-modal__header__bottom">
										<form id="tfaForm" data-testid="tfaForm" onSubmit={handleSubmit(tfaVerifierHanlder)}>
											<Input
												maxWidth="232"
												name="otp"
												placeholder="000000"
												register={register({
													required: "This field is mandatory",
													minLength: {
														value: 6,
														message: "Please enter a valid OTP"
													}
												})}
												error={errors.otp}
												errorMessage={errors.otp?.message}
												maxLength={6}
												type="number"
												data-testid="otp-input"
											/>
										</form>
									</div>
								</div>
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button type="submit" id="disable-2fa-authentication" text="Verify" form="tfaForm" disabled={!isValid} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={learnMoreMekerCheckerModal} variant="medium" close={hideLearnMoreMekerCheckerModal}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
								<div className="card-configure__header__left">
									<div className="card-configure__title">
										<div className="card-configure__title__left">
											<div className='maker-checker-modal__icon'><InformationCircle /></div>
										</div>
										<div className="card-configure__title__right">
											<div className="card-configure__title__right__top">
												About Maker Checker Functionality
											</div>
										</div>
									</div>
								</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="maker-checker-modal__content">
								This two-step process involves a <b>Maker</b> initiating the transaction and a <b>Checker</b> thoroughly examining it. By having an independent reviewer analyze each transaction meticulously, we reduce the risk of unnoticed errors, increasing the likelihood of detecting and rectifying mistakes.
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button full maxWidth="100" round submit={() => hideLearnMoreMekerCheckerModal()} text="Okay, got it!" />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={verifyMakerCheckerModal} variant="medium" close={hideVerifyMakerCheckerModal}>
				<CardConfigure addHeaderClass="maker-checker-modal__header" >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div>
								<div className="card-configure__header__left">
									<div className="card-configure__title">
										<div className="card-configure__title__left">
											<div className='maker-checker-modal__icon'><ExclamationCircleLight /></div>
										</div>
										<div className="card-configure__title__right">
											<div className="card-configure__title__right__top">
												Enable Maker Checker Functionality
											</div>
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="maker-checker-modal__content">
									Are you sure you want to enable approval workflow for Data Element Sharing and E-Transferable Records for all the Pitstop users?
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Left>
							<div className='maker-checker-modal__confirmation_actions'>
								<Button data-testid="maker-checker-decline" ghost noborder round submit={() => hideVerifyMakerCheckerModal()} text="Cancel" />
							</div>
						</CardConfigure.Footer.Left>
						<CardConfigure.Footer.Right>
							<div className='maker-checker-modal__confirmation_actions'>
								<Button data-testid="maker-checker-agree" full round maxWidth="100" submit={() => acceptVerifyMakerCheckerModal()} text="Proceed" />
							</div>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={disableMakerCheckerModal} variant='medium' close={hideDisableMakerCheckerModal}>
				<CardConfigure addHeaderClass="maker-checker-modal__header" >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div>
								<div className="card-configure__header__left">
									<div className="card-configure__title">
										<div className="card-configure__title__left">
											<div className='maker-checker-modal__icon maker-checker-modal__disable'><ExclamationCircleLight /></div>
										</div>
										<div className="card-configure__title__right">
											<div className="card-configure__title__right__top">
												Disable Maker Checker Functionality
											</div>
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="maker-checker-modal__content">
								{
									noPendingMakerCheckerTranasction ?
										"Are you sure you want to disable Maker Checker Functionality for all of the Pitstop user?" :
										"There are still pending approval requests in the request list. To disable this functionality, please review and either approve or reject all pending approval requests in the Pitstop portal."
								}

							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						{
							noPendingMakerCheckerTranasction && (
								<CardConfigure.Footer.Left>
									<Button data-testid="disable-maker-checker-decline" ghost noborder round submit={() => hideDisableMakerCheckerModal()} text="Cancel" />
								</CardConfigure.Footer.Left>
							)
						}
						<CardConfigure.Footer.Right>
							{
								noPendingMakerCheckerTranasction ? (
									<Button data-testid="disable-maker-checker-agree" error full round maxWidth="100" submit={() => acceptDisableMakerCheckerModal()} text="Proceed" />
								) : (
									<Button data-testid="disable-maker-checker-agree" error full round maxWidth="100" submit={() => hideDisableMakerCheckerModal()} text="Cancel" />
								)
							}
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={errorUserModal} variant='small' close={hideErrorUserModal}>
				<CardConfigure addHeaderClass="maker-checker-modal__header">
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div>
								<div className="card-configure__header__left">
									<div className="card-configure__title">
										<div className="card-configure__title__left">
											<div className='maker-checker-modal__icon maker-checker-modal__disable'><IconWarningTrash /></div>
										</div>
										<div className="card-configure__title__right">
											<div className="card-configure__title__right__top">
												Unable to Delete Account 
											</div>
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="maker-checker-modal__content">
								{errorUserMessage}
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<div className='maker-checker-modal__confirmation_actions'>
									<Button full round error maxWidth="100" text="Okay" id="delete-user-submit-button" submit={() => {
										hideErrorUserModal();
									}} />
								</div>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={isAssignGroupVisible} variant='small' close={hideAssignGroupModal} dismissClickOutsideModal={false}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left card-configure__title__left--background">
										<AssignGroupIcon />
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Assign Group
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="user-management-modal">
								<div className="user-management-modal__top">
								<span className="DS__input-label__asterisk">*</span> Group
								</div>
								<div>
									<InputDropdownDS
										value={dropdownSearch.dataElementSearch}
										onChange={dropdownSearchHandler}
										name={'groupSearch'}
										dropdownOption={transformedGroups}
										placeholder={"Select an option..."}
										onSelectDropdownItem={setChipValues}
										dropdownSelectedOption={chipValues}
										multipleSelection={true}
										fullWidth={true}
										showTooltipInfo={true}
									/>
								</div>
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer className="user-management-modal__footer">
						<CardConfigure.Footer.Right>
							<Button
								text="Cancel"
								ghost
								submit={() => {	
									hideAssignGroupModal();	
									setChipValues([]);
								}}
								addClass="user-management-modal__footer__cancel"
								noborder
								round
							/>
							<Button
								text="Confirm"
								disabled={submitLoading || chipValues.length === 0}
								submit={() => {
									assignSelectedUsersToTheSelectedGroups();
								}}
								round
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>
			
			<Toast toastList={toastList} />

			{
				submitLoading && (
					ReactDOM.createPortal(
						<div className='modal white'>
							<div className='loading'>
								<img src={assetUrl("/img/loading.gif")} />
							</div>
						</div>
						, document.body
					)
				)
			}
		</>
	);
};

export default UserManagement;
